import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import { useActiveGroup } from '../hooks/useActiveGroup';
import { useAuth } from '../hooks/useAuth';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import { 
  Plus, 
  Users, 
  Star, 
  Pencil,
  Trash2, 
  AlertCircle,
  Archive,
  ChevronDown,
  ChevronUp,
  Trophy,
  Calendar,
  ToggleLeft,
  ToggleRight,
  MessageSquare,
  Search,
  Share2,
  Copy,
  Check,
  X,
  Loader2
} from 'lucide-react';
import PlayerForm from '../components/players/PlayerForm';
import OccurrenceModal from '../components/occurrences/OccurrenceModal';
import PlanLimitModal from '../components/PlanLimitModal';
import { getProxiedImageUrl } from '../utils/imageUtils';
import { gerarCaracteristicasPorNivel } from '../utils/playerUtils';
import { FaWhatsapp } from 'react-icons/fa';

interface Player {
  id: string;
  nome: string;
  telefone: string | null;
  nivel: number;
  posicao: string | null;
  ativo: boolean;
  idade: number;
  tipo: string;
  foto_url: string | null;
  referencia: string | null;
  grupo_id?: string;
  criado_em?: string;
  grupo: {
    nome: string;
  };
  _count?: {
    ocorrencias: number;
    jogos: number;
  };
  payment_status?: {
    paid: boolean;
    last_payment?: string;
  };
  ocorrencias_count?: number;
}

const PlayersPage: React.FC = () => {
  const navigate = useNavigate();
  const { supabase } = useSupabase();
  const { activeGroup } = useActiveGroup();
  const { user } = useAuth();
  const { canAddPlayer } = usePlanFeatures();
  
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [showOccurrenceModal, setShowOccurrenceModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planModalMessage, setPlanModalMessage] = useState('');
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'todos' | 'ativos' | 'avulsos' | 'desativados'>('todos');
  const [showShareModal, setShowShareModal] = useState(false);
  const [registrationLink, setRegistrationLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchPlayers = async () => {
    if (!user) return;

    try {
      setError(null);
      
      // First, get active group from profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('grupo_ativo')
        .eq('id', user.id)
        .single();

      if (profileData?.grupo_ativo) {
        setSelectedGroupId(profileData.grupo_ativo);

        // Get current month range
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

        // First, get all players
        const { data: allPlayers, error: playersError } = await supabase
          .from('jogadores')
          .select(`
            *,
            grupo:grupos(nome)
          `)
          .eq('grupo_id', profileData.grupo_ativo)
          .order('nome');

        if (playersError) throw playersError;

        // Get occurrences count for each player
        const playersWithOccurrences = await Promise.all(allPlayers?.map(async (player) => {
          const { count } = await supabase
            .from('ocorrencias_jogadores')
            .select('*', { count: 'exact', head: true })
            .eq('jogador_id', player.id)
            .eq('arquivado', false);

          return {
            ...player,
            ocorrencias_count: count || 0
          };
        }) || []);

        // Then, get payments for the current month
        const { data: payments, error: paymentsError } = await supabase
          .from('financas')
          .select('jogador_id, data')
          .eq('grupo_id', profileData.grupo_ativo)
          .eq('tipo', 'receita')
          .gte('data', startDate)
          .lte('data', endDate);

        if (paymentsError) throw paymentsError;

        // Map payments to players
        const playersWithPayments = playersWithOccurrences.map(player => ({
          ...player,
          payment_status: {
            paid: payments?.some(p => p.jogador_id === player.id) || false,
            last_payment: payments?.find(p => p.jogador_id === player.id)?.data
          }
        }));

        // Fetch participation in games
        const { data: gamesData, error: gamesError } = await supabase
          .from('jogos_jogadores')
          .select(`
            jogador_id,
            jogo:jogos(id)
          `);

        if (gamesError) throw gamesError;

        // Process data to add counts e garantir características
        const processedPlayers = playersWithPayments.map(player => {
          const playerGames = gamesData?.filter(
            g => g.jogador_id === player.id
          ) || [];

          return {
            ...player,
            caracteristicas: player.caracteristicas || gerarCaracteristicasPorNivel(player.nivel),
            _count: {
              ocorrencias: player.ocorrencias_count || 0,
              jogos: playerGames.length
            }
          };
        });

        setPlayers(processedPlayers);
      }
    } catch (error) {
      console.error('Erro ao carregar jogadores:', error);
      setError('Erro ao carregar jogadores. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, [supabase, user]);

  const generateRegistrationLink = async () => {
    if (!selectedGroupId || !user) {
      setError('Grupo ou usuário não encontrado');
      return;
    }

    setGeneratingLink(true);
    try {
      // Verificar se já existe um token para este grupo
      const { data: existingGroup, error: checkError } = await supabase
        .from('grupos')
        .select('public_registration_token')
        .eq('id', selectedGroupId)
        .single();

      if (checkError) throw checkError;

      let token = existingGroup.public_registration_token;

      // Se não existe token, criar um novo
      if (!token) {
        token = crypto.randomUUID();
        
        const { error: updateError } = await supabase
          .from('grupos')
          .update({ public_registration_token: token })
          .eq('id', selectedGroupId);

        if (updateError) throw updateError;
      }

      const link = `${window.location.origin}/cadastrar-jogador/${token}`;
      setRegistrationLink(link);
      setShowShareModal(true);

      // Copiar automaticamente para o clipboard
      try {
        await navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch (clipboardError) {
        console.warn('Erro ao copiar para clipboard:', clipboardError);
      }
    } catch (error) {
      console.error('Erro ao gerar link:', error);
      setError('Erro ao gerar link de cadastro');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyRegistrationLink = async () => {
    if (!registrationLink) return;

    try {
      await navigator.clipboard.writeText(registrationLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar link:', error);
    }
  };

  const shareOnWhatsApp = () => {
    if (!registrationLink || !activeGroup) return;

    const message = `🏆 *${activeGroup.nome}*

Olá! Você foi convidado para se cadastrar como jogador no nosso time.

📝 *Link de cadastro:*
${registrationLink}

Clique no link acima para preencher seus dados e fazer parte do time!

_Enviado via Presida.Club_`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  const handleToggleActive = async (playerId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('jogadores')
        .update({ ativo: !currentStatus })
        .eq('id', playerId);

      if (error) throw error;

      // Update local state
      setPlayers(players.map(player =>
        player.id === playerId ? { ...player, ativo: !currentStatus } : player
      ));
    } catch (error) {
      console.error('Erro ao atualizar status do jogador:', error);
      setError('Erro ao atualizar status do jogador');
    }
  };

  const handleDelete = async (playerId: string) => {
    if (!confirm('Tem certeza que deseja excluir este jogador? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('jogadores')
        .delete()
        .eq('id', playerId);

      if (error) throw error;

      // Update local state
      setPlayers(players.filter(player => player.id !== playerId));
    } catch (error) {
      console.error('Erro ao excluir jogador:', error);
      setError('Erro ao excluir jogador');
    }
  };

  const handleAddPlayer = async () => {
    if (!activeGroup) return;

    const { allowed, message } = await canAddPlayer(players.length);
    if (!allowed) {
      setPlanModalMessage(message || 'Limite de jogadores atingido');
      setShowPlanModal(true);
      return;
    }

    setShowForm(true);
  };

  const filteredPlayers = players.filter(player => {
    const matchesSearch = player.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         player.telefone?.includes(searchTerm) ||
                         player.posicao?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'todos' || 
                         (statusFilter === 'ativos' && player.ativo && player.tipo === 'ativo') ||
                         (statusFilter === 'avulsos' && player.ativo && player.tipo === 'avulso') ||
                         (statusFilter === 'desativados' && !player.ativo);

    return matchesSearch && matchesStatus;
  });

  if (!activeGroup) {
    return (
      <div className="text-center py-12">
        <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Nenhum grupo selecionado</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Selecione um grupo ativo para gerenciar jogadores
        </p>
        <Link to="/grupos" className="btn-primary">
          Ir para Grupos
        </Link>
      </div>
    );
  }

  return (
    <div>
      <header className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Jogadores</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Gerencie os jogadores do seu grupo
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={generateRegistrationLink}
            className="btn-outline"
            disabled={generatingLink}
          >
            {generatingLink ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Share2 className="w-4 h-4 mr-2" />
            )}
            Link de Cadastro
          </button>
          <button onClick={handleAddPlayer} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Novo Jogador
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar jogadores..."
              className="input pl-10 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter('todos')}
              className={`btn ${statusFilter === 'todos' ? 'btn-primary' : 'btn-outline'}`}
            >
              Todos
            </button>
            <button
              onClick={() => setStatusFilter('ativos')}
              className={`btn ${statusFilter === 'ativos' ? 'btn-primary' : 'btn-outline'}`}
            >
              Ativos
            </button>
            <button
              onClick={() => setStatusFilter('avulsos')}
              className={`btn ${statusFilter === 'avulsos' ? 'btn-primary' : 'btn-outline'}`}
            >
              Avulsos
            </button>
            <button
              onClick={() => setStatusFilter('desativados')}
              className={`btn ${statusFilter === 'desativados' ? 'btn-primary' : 'btn-outline'}`}
            >
              Inativos
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : filteredPlayers.length === 0 ? (
        <div className="card p-8 text-center">
          <Users className="w-16 h-16 text-primary-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">
            {searchTerm || statusFilter !== 'todos' 
              ? 'Nenhum jogador encontrado' 
              : 'Nenhum jogador cadastrado'
            }
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {searchTerm || statusFilter !== 'todos'
              ? 'Tente ajustar os filtros de busca'
              : 'Adicione seu primeiro jogador para começar'
            }
          </p>
          {(!searchTerm && statusFilter === 'todos') && (
            <button onClick={handleAddPlayer} className="btn-primary">
              Adicionar primeiro jogador
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPlayers.map((player) => (
            <div
              key={player.id}
              className={`card hover:shadow-md transition-shadow cursor-pointer ${
                !player.ativo ? 'opacity-75' : ''
              }`}
              onClick={() => navigate(`/jogadores/${player.id}`)}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {player.foto_url ? (
                      <img
                        src={getProxiedImageUrl(player.foto_url)}
                        alt={player.nome}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center text-lg font-medium">
                        {player.nome.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <Link
                        to={`/jogadores/${player.id}`}
                        className="hover:text-primary-600 transition-colors"
                      >
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          {player.nome}
                        </h3>
                      </Link>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {player.idade} anos • {player.tipo}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleActive(player.id, player.ativo);
                      }}
                      className={`transition-colors ${
                        player.ativo
                          ? 'text-primary-500 hover:text-primary-700'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      title={player.ativo ? 'Desativar' : 'Ativar'}
                    >
                      {player.ativo ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  {player.posicao && (
                    <div className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-gray-500" />
                      <span className="text-sm capitalize">{player.posicao}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {Array.from({ length: player.nivel }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500">
  Nível {{
    1: 'Iniciante',
    2: 'Básico',
    3: 'Intermediário',
    4: 'Avançado',
    5: 'Profissional',
  }[player.nivel] || 'Desconhecido'}
</span>

                  </div>

                 {player.telefone && (
  <button
    onClick={(e) => {
      e.stopPropagation();
      const phoneStr = player.telefone || '';
      let whatsappUrl = '';

      if (phoneStr.startsWith('+')) {
        // Formato internacional
        const cleanPhone = phoneStr.replace(/\D/g, '');
        whatsappUrl = `https://wa.me/${cleanPhone}`;
      } else {
        // Formato brasileiro sem código de país
        const cleanPhone = phoneStr.replace(/\D/g, '');
        whatsappUrl = `https://wa.me/55${cleanPhone}`;
      }

      window.open(whatsappUrl, '_blank');
    }}
    className="flex items-center gap-2 text-green-600 hover:text-green-700 transition-colors hover:underline"
    title="Abrir conversa no WhatsApp"
  >
    <FaWhatsapp className="w-5 h-5" />
    <span className="text-sm">{player.telefone}</span>
  </button>
)}

 

                  {player.payment_status && (
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        player.payment_status.paid ? 'bg-green-500' : 'bg-red-500'
                      }`} />
                      <span className="text-sm">
                        {player.payment_status.paid ? 'Mensalidade paga' : 'Mensalidade pendente'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t dark:border-dark-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{player._count?.jogos || 0} jogos</span>
                      </div>
                      {(player._count?.ocorrencias || 0) > 0 && (
                        <div className="flex items-center gap-1">
                          <AlertCircle className="w-4 h-4 text-yellow-500" />
                          <span>{player._count.ocorrencias} ocorrências</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPlayer(player);
                          setShowOccurrenceModal(true);
                        }}
                        className="p-2 text-yellow-500 hover:text-yellow-700 transition-colors"
                        title="Nova ocorrência"
                      >
                        <MessageSquare className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/jogadores/${player.id}/editar`);
                        }}
                        className="p-2 text-blue-500 hover:text-blue-700 transition-colors"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(player.id);
                        }}
                        className="p-2 text-red-500 hover:text-red-700 transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Player Form Modal */}
      {showForm && selectedGroupId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b dark:border-dark-border">
              <h2 className="text-xl font-bold">Novo Jogador</h2>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-dark-light rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <PlayerForm
                groupId={selectedGroupId}
                onCancel={() => setShowForm(false)}
                onSuccess={() => {
                  setShowForm(false);
                  fetchPlayers();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Share Registration Link Modal */}
      {showShareModal && registrationLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-dark-paper rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b dark:border-dark-border">
              <h2 className="text-xl font-bold">Link de Cadastro</h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-dark-light rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Link de cadastro público:
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={registrationLink}
                    readOnly
                    className="input flex-1"
                    onClick={(e) => e.currentTarget.select()}
                  />
                  <button
                    onClick={copyRegistrationLink}
                    className="btn-outline"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                {copied && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                    Link copiado para o clipboard!
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Compartilhe este link para que outras pessoas possam se cadastrar como jogadores no seu grupo.
                </p>

                <button
                  onClick={shareOnWhatsApp}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Compartilhar no WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Occurrence Modal */}
      {showOccurrenceModal && selectedPlayer && (
        <OccurrenceModal
          isOpen={showOccurrenceModal}
          onClose={() => {
            setShowOccurrenceModal(false);
            setSelectedPlayer(null);
          }}
          mainPlayer={selectedPlayer}
          groupId={selectedPlayer.grupo_id || selectedGroupId || ''}
        />
      )}

      {/* Plan Limit Modal */}
      <PlanLimitModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        message={planModalMessage}
      />
    </div>
  );
};

export default PlayersPage;